import type { ReactNode } from 'react';
import { type FieldValues, useController } from 'react-hook-form';

import {
  DropdownSingleSelectAsync,
  type DropdownSingleSelectAsyncProps,
} from '~/chords/DropdownSingleSelectAsync/DropdownSingleSelectAsync';
import { FormField, type FormFieldProps } from '~/chords/FormField';
import {
  SortableMultiSelect,
  type SortableMultiSelectSecondaryAction,
} from '~/chords/SortableMultiSelect/SortableMultiSelect';

type FormFieldDropdownSortableMultiSelectAsyncProps<
  T,
  K,
  TFormFields extends FieldValues,
> = Omit<
  FormFieldProps<TFormFields>,
  | 'children'
  | 'dangerouslyOverrideClassName'
  | 'triggerClassName'
  | 'fieldMessageClassName'
  | 'labelAsSpan'
> &
  Omit<
    DropdownSingleSelectAsyncProps<T, K>,
    'onChange' | 'defaultValue' | 'resolveFn' | 'defaultValueErrorMessage'
  > & {
    onValueChange?: (items: T[]) => void;
    getItemImage?: (item: T) => string | undefined;
    renderContent?: (item: T) => ReactNode;
    secondaryActions?: SortableMultiSelectSecondaryAction<T>[];
    removeLabel?: string;
  };

export function FormFieldDropdownSortableMultiSelectAsync<
  T,
  K,
  TFormFields extends FieldValues = FieldValues,
>(props: FormFieldDropdownSortableMultiSelectAsyncProps<T, K, TFormFields>) {
  const {
    name,
    label,
    disabled,
    readOnly,
    labelAddon,
    showErrorMessage,
    onValueChange,
    getOptionValue,
    getOptionLabel,
    onOpenChange,
    queryFn,
    getItemImage,
    renderContent,
    secondaryActions,
    removeLabel,
    ...dropdownProps
  } = props;

  const {
    field: { ref: _ref, value, onChange, onBlur },
    fieldState: { invalid },
  } = useController<TFormFields>({ name, disabled });

  const selectedItems = (value ?? []) as T[];

  return (
    <FormField
      name={name}
      label={label}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
      showErrorMessage={showErrorMessage}
    >
      <SortableMultiSelect<T>
        value={selectedItems}
        onValueChange={(items) => {
          onChange(items);
          onBlur();
          onValueChange?.(items);
        }}
        getItemLabel={getOptionLabel}
        getItemValue={(item) => String(getOptionValue(item))}
        getItemImage={getItemImage}
        renderContent={renderContent}
        secondaryActions={secondaryActions}
        removeLabel={removeLabel}
        disabled={disabled || readOnly}
        renderDropdown={({
          onSelect,
          selectedValues,
          resetKey,
          disabled: isDisabled,
        }) => (
          <DropdownSingleSelectAsync<T, K>
            key={resetKey}
            id={name}
            {...dropdownProps}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            queryFn={async (search) => {
              const results = await queryFn(search);

              return results.filter(
                (item) => !selectedValues.has(String(getOptionValue(item))),
              );
            }}
            onChange={onSelect}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                onBlur();
              }

              onOpenChange?.(isOpen);
            }}
            state={invalid ? 'error' : 'default'}
            disabled={isDisabled}
          />
        )}
      />
    </FormField>
  );
}

FormFieldDropdownSortableMultiSelectAsync.displayName =
  'FormFieldDropdownSortableMultiSelectAsync';
